import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Lock, MapPin, Link2, ExternalLink, MoreVertical, UserPlus } from 'lucide-react'
import { accountsApi, adminApi, groupsApi } from '@/api'
import { useAuthStore } from '@/store/auth.store'
import { useFeedStore } from '@/store/feed.store'
import { useActiveGroupStore } from '@/store/activeGroup.store'
import { useToast } from '@/components/common/Toast'
import { Avatar } from '@/components/profile/Avatar'
import { FeedList, FeedTypeChips } from '@/components/feed'
import { GroupMemberRow } from '@/components/groups/GroupMemberRow'
import { InviteModal } from '@/components/groups/InviteModal'
import { SYSTEM_BADGE_SLUGS } from '@olimhub/shared'
import type { FeedItem } from '@olimhub/shared'

type GroupTab = 'feed' | 'members'

function friendlyGroupError(err: unknown, t: (key: string) => string): string {
  const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.status
  if (status === 409) return t('groups.error_need_admin')
  if (status === 403) return t('groups.error_forbidden')
  return t('groups.error_generic')
}

export function GroupPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { account: authAccount, isAuthenticated, hasCapability } = useAuthStore()
  const { guestFeedLanguages, groupSelectedTypes, toggleGroupType } = useFeedStore()
  const selectedTypes = groupSelectedTypes[slug ?? ''] ?? []
  const feedLangs = isAuthenticated
    ? (authAccount?.feedLanguages?.length ? authAccount.feedLanguages : undefined)
    : (guestFeedLanguages.length ? guestFeedLanguages : undefined)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<GroupTab>('feed')
  const [inviteOpen, setInviteOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [working, setWorking] = useState(false)
  const [suggestedWorking, setSuggestedWorking] = useState(false)

  // viewerMembershipStatus/isClosedToViewer are per-viewer — the key must
  // include who's asking, or switching accounts in the same tab can serve a
  // stale response cached under a different account (found via manual QA).
  const viewerKey = authAccount?.id ?? 'anon'

  const { data: group, isLoading: groupLoading } = useQuery({
    queryKey: ['group', slug, viewerKey],
    queryFn: () => groupsApi.getBySlug(slug!),
    enabled: !!slug,
  })

  const gated = !!group && group.isClosedToViewer

  const { data: activeMembers } = useQuery({
    queryKey: ['group-members', slug, 'active'],
    queryFn: () => groupsApi.getMembers(slug!, 'active'),
    enabled: !!slug && !!group && !gated,
  })

  const viewerMember = activeMembers?.find((m) => m.account.id === authAccount?.id)
  const viewerRoleBadge = viewerMember?.account.badges.find((ab) => ab.badge.scope === 'group' && ab.groupId === group?.id)
  const isGroupAdmin = viewerRoleBadge?.badge.slug === SYSTEM_BADGE_SLUGS.GROUP_ADMIN
  const isGroupEditor = viewerRoleBadge?.badge.slug === SYSTEM_BADGE_SLUGS.GROUP_EDITOR
  const canPostAsGroup = isGroupAdmin || isGroupEditor
  const canManageMembers = isGroupAdmin

  const { data: pendingMembers } = useQuery({
    queryKey: ['group-members', slug, 'requested'],
    queryFn: () => groupsApi.getMembers(slug!, 'requested'),
    enabled: !!slug && canManageMembers,
  })

  // Tells the global FloatingPostButton (rendered once by Layout, outside
  // this page) to open the composer pre-linked to this group. Only while
  // the viewer is an active member — matches the backend's own requirement
  // for groupTagSlug (post_as_group implies membership too).
  const setActiveGroup = useActiveGroupStore((s) => s.setActiveGroup)
  useEffect(() => {
    if (!slug || !group || !viewerMember) {
      setActiveGroup(null)
      return
    }
    setActiveGroup({ slug, name: group.name, canPostAsGroup, isOpen: group.isOpen })
    return () => setActiveGroup(null)
  }, [slug, group, viewerMember, canPostAsGroup, setActiveGroup])

  const {
    data: feedData,
    hasNextPage: feedHasMore,
    fetchNextPage: feedFetchNext,
    isFetching: feedIsFetching,
    isFetchingNextPage: feedIsFetchingMore,
  } = useInfiniteQuery({
    queryKey: ['group-feed', slug, viewerKey, feedLangs?.join(',') ?? '', selectedTypes],
    queryFn: ({ pageParam }) => groupsApi.getFeed(slug!, pageParam as string | undefined, feedLangs, selectedTypes),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!slug && !!group && !gated && tab === 'feed',
  })
  const feedItems: FeedItem[] = feedData?.pages.flatMap((p) => p.items) ?? []

  async function invalidateGroup() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['group', slug] }),
      queryClient.invalidateQueries({ queryKey: ['group-members', slug] }),
    ])
  }

  async function handleRequestJoin() {
    if (!slug) return
    setWorking(true)
    try {
      await groupsApi.requestJoin(slug)
      toast.success(t('groups.request_sent'))
      await invalidateGroup()
    } catch (err) {
      toast.error(friendlyGroupError(err, t))
    } finally {
      setWorking(false)
    }
  }

  // Non-member alternative to joining — feeds.service.ts's getFollowingFeed
  // already treats following the mirror account the same as active
  // membership, so this is a real way to get the group's cross-posted
  // content into Following without joining.
  async function handleFollow(isFollowing: boolean) {
    if (!group?.mirrorAccountUsername) return
    setWorking(true)
    try {
      if (isFollowing) {
        await accountsApi.unfollow(group.mirrorAccountUsername)
      } else {
        await accountsApi.follow(group.mirrorAccountUsername)
      }
      await Promise.all([invalidateGroup(), queryClient.invalidateQueries({ queryKey: ['feed'] })])
    } catch (err) {
      toast.error(friendlyGroupError(err, t))
    } finally {
      setWorking(false)
    }
  }

  // Site-admin-only: pins the group's mirror account into the account-level
  // Follow Suggestions feature (accounts.isSuggested) — same mechanism used
  // for regular accounts, since a group's mirror account is a normal row in
  // the accounts table.
  async function handleToggleSuggested(newValue: boolean) {
    if (!group) return
    setSuggestedWorking(true)
    try {
      await adminApi.toggleSuggested(group.mirrorAccountId, newValue)
      await invalidateGroup()
      toast.success(newValue ? t('suggestions.pinned_toast') : t('suggestions.unpinned_toast'))
    } catch {
      toast.error(t('suggestions.pin_error_toast'))
    } finally {
      setSuggestedWorking(false)
    }
  }

  async function handleRespond(accept: boolean, accountId?: string) {
    if (!slug) return
    setWorking(true)
    try {
      await groupsApi.respond(slug, { accept, accountId })
      await invalidateGroup()
    } catch (err) {
      toast.error(friendlyGroupError(err, t))
    } finally {
      setWorking(false)
    }
  }

  async function handleLeave() {
    if (!slug) return
    setWorking(true)
    setMenuOpen(false)
    try {
      await groupsApi.leave(slug)
      toast.success(t('groups.left_group'))
      await invalidateGroup()
    } catch (err) {
      toast.error(friendlyGroupError(err, t))
    } finally {
      setWorking(false)
    }
  }

  async function handleGrant(accountId: string, badgeSlug: 'group_admin' | 'group_editor') {
    if (!slug) return
    setWorking(true)
    try {
      await groupsApi.grantBadge(slug, accountId, badgeSlug)
      await invalidateGroup()
    } catch (err) {
      toast.error(friendlyGroupError(err, t))
    } finally {
      setWorking(false)
    }
  }

  async function handleRevoke(accountBadgeId: string) {
    if (!slug) return
    setWorking(true)
    try {
      await groupsApi.revokeBadge(slug, accountBadgeId)
      await invalidateGroup()
    } catch (err) {
      toast.error(friendlyGroupError(err, t))
    } finally {
      setWorking(false)
    }
  }

  if (groupLoading) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-faint)' }}>{t('common.loading')}</div>
  }
  if (!group) {
    return <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-faint)' }}>{t('groups.not_found')}</div>
  }

  return (
    <div>
      {/* Header */}
      <div style={{ background: group.headerUrl ? undefined : 'var(--surface-2)', height: 100, backgroundImage: group.headerUrl ? `url(${group.headerUrl})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center' }} />

      <div style={{ padding: '0 16px 14px' }}>
        <div style={{ marginTop: -32 }}>
          <Avatar name={group.name} avatarUrl={group.avatarUrl} accountType="group" size="lg" style={{ boxShadow: '0 0 0 4px var(--surface), var(--shadow-card)' }} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 700, fontSize: 'var(--fs-lg)', color: 'var(--text-strong)' }}>{group.name}</span>
          {!group.isOpen && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 'var(--radius-pill)', background: 'var(--surface-2)', color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 600 }}>
              <Lock size={12} /> {t('groups.closed')}
            </span>
          )}
        </div>

        {group.description && (
          <p style={{ margin: '6px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>{group.description}</p>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
          {group.region && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
              <MapPin size={12} /> {t(`groups.region.${group.region}`)}
            </span>
          )}
          {group.links && Object.entries(group.links).filter(([, v]) => v).map(([key, url]) => (
            <a key={key} href={url} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--brand)', textDecoration: 'none' }}>
              <Link2 size={12} /> {key} <ExternalLink size={10} />
            </a>
          ))}
        </div>

        {hasCapability('grant_badges') && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
              {t('suggestions.pin_as_suggested')}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={group.mirrorAccountIsSuggested ?? false}
              disabled={suggestedWorking}
              onClick={() => void handleToggleSuggested(!(group.mirrorAccountIsSuggested ?? false))}
              style={{
                width: 44,
                height: 26,
                borderRadius: 13,
                border: 'none',
                background: group.mirrorAccountIsSuggested ? 'var(--brand)' : 'var(--border-strong)',
                position: 'relative',
                cursor: suggestedWorking ? 'not-allowed' : 'pointer',
                opacity: suggestedWorking ? 0.6 : 1,
                flexShrink: 0,
                transition: 'background var(--dur-fast) var(--ease-out)',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 3,
                  insetInlineStart: group.mirrorAccountIsSuggested ? 21 : 3,
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'inset-inline-start var(--dur-fast) var(--ease-out)',
                }}
              />
            </button>
          </div>
        )}

        {/* Membership action */}
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          {!isAuthenticated ? null : group.viewerMembershipStatus === null ? (
            <>
              <button type="button" disabled={working} onClick={() => void handleRequestJoin()} style={primaryBtn}>
                {t('groups.request_to_join')}
              </button>
              <button type="button" disabled={working} onClick={() => void handleFollow(!!group.isFollowingMirror)} style={secondaryBtn}>
                {group.isFollowingMirror ? t('profile.following') : t('profile.follow')}
              </button>
            </>
          ) : group.viewerMembershipStatus === 'requested' ? (
            <span style={pendingPill}>{t('groups.request_pending')}</span>
          ) : group.viewerMembershipStatus === 'invited' ? (
            <>
              <button type="button" disabled={working} onClick={() => void handleRespond(true)} style={primaryBtn}>{t('groups.accept')}</button>
              <button type="button" disabled={working} onClick={() => void handleRespond(false)} style={secondaryBtn}>{t('groups.decline')}</button>
            </>
          ) : (
            <div style={{ position: 'relative' }}>
              <button type="button" onClick={() => setMenuOpen((v) => !v)} style={iconBtn} aria-label={t('groups.member_menu')}>
                <MoreVertical size={18} />
              </button>
              {menuOpen && (
                <div style={{ position: 'absolute', top: '100%', insetInlineStart: 0, marginTop: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-modal)', zIndex: 'var(--z-sheet)', minWidth: 140 }}>
                  {isGroupAdmin && (
                    <button type="button" onClick={() => navigate(`/groups/${slug}/settings`)} style={{ display: 'block', width: '100%', padding: '10px 14px', border: 'none', background: 'none', textAlign: 'start', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
                      {t('groups.edit_settings')}
                    </button>
                  )}
                  <button type="button" onClick={() => void handleLeave()} style={{ display: 'block', width: '100%', padding: '10px 14px', border: 'none', background: 'none', textAlign: 'start', cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--danger)' }}>
                    {t('groups.leave')}
                  </button>
                </div>
              )}
            </div>
          )}

          {canPostAsGroup && (
            <button type="button" onClick={() => setInviteOpen(true)} style={secondaryBtn}>
              <UserPlus size={14} style={{ marginInlineEnd: 4 }} /> {t('groups.invite')}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
        {(['feed', 'members'] as GroupTab[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            style={{
              flex: 1,
              padding: '10px 0',
              border: 'none',
              borderBottom: tab === key ? '2px solid var(--brand)' : '2px solid transparent',
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 'var(--fs-sm)',
              color: tab === key ? 'var(--text-strong)' : 'var(--text-faint)',
            }}
          >
            {key === 'feed' ? t('groups.tab_feed') : t('groups.tab_members')}
          </button>
        ))}
      </div>

      {gated ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 24px', textAlign: 'center' }}>
          <Lock size={40} strokeWidth={1.5} style={{ color: 'var(--text-faint)', marginBottom: 12 }} />
          <p style={{ margin: 0, fontFamily: 'var(--font-sans)', fontWeight: 600, fontSize: 'var(--fs-sm)', color: 'var(--text-strong)' }}>
            {t('groups.locked_title')}
          </p>
          <p style={{ margin: '4px 0 0', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', color: 'var(--text-faint)' }}>
            {t('groups.locked_hint')}
          </p>
        </div>
      ) : tab === 'feed' ? (
        <>
          <FeedTypeChips selectedTypes={selectedTypes} onToggle={(type) => toggleGroupType(slug!, type)} />
          <FeedList
            items={feedItems}
            hasMore={!!feedHasMore}
            isLoading={feedIsFetching && feedItems.length === 0}
            isFetchingMore={feedIsFetchingMore}
            onLoadMore={() => void feedFetchNext()}
            hideGroupAttribution
            viewerIsGroupAdmin={isGroupAdmin}
            emptyNode={
              <div style={{ padding: '56px 24px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-faint)' }}>
                {selectedTypes.length > 0 ? t('feed.empty_filtered') : t('groups.no_posts_yet')}
              </div>
            }
          />
        </>
      ) : (
        <div>
          {canManageMembers && pendingMembers && pendingMembers.length > 0 && (
            <div style={{ padding: '10px 16px 0' }}>
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 4 }}>
                {t('groups.pending_requests')}
              </div>
              {pendingMembers.map((m) => (
                <GroupMemberRow
                  key={m.account.id}
                  member={m}
                  groupId={group.id}
                  canManageBadges={false}
                  working={working}
                  onAccept={(accountId) => void handleRespond(true, accountId)}
                  onDecline={(accountId) => void handleRespond(false, accountId)}
                />
              ))}
              <div style={{ borderBottom: '1px solid var(--border)', margin: '8px 0' }} />
            </div>
          )}

          {(activeMembers ?? []).map((m) => (
            <GroupMemberRow
              key={m.account.id}
              member={m}
              groupId={group.id}
              canManageBadges={isGroupAdmin}
              working={working}
              onPromoteToEditor={(accountId) => void handleGrant(accountId, 'group_editor')}
              onPromoteToAdmin={(accountId) => void handleGrant(accountId, 'group_admin')}
              onRevokeBadge={(accountBadgeId) => void handleRevoke(accountBadgeId)}
            />
          ))}
        </div>
      )}

      {inviteOpen && <InviteModal groupSlug={slug!} onClose={() => setInviteOpen(false)} />}
    </div>
  )
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 'var(--radius-pill)',
  border: 'none',
  background: 'var(--brand)',
  color: 'var(--on-brand)',
  fontFamily: 'var(--font-sans)',
  fontWeight: 700,
  fontSize: 'var(--fs-sm)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
}

const secondaryBtn: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--border)',
  background: 'none',
  color: 'var(--text-strong)',
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 'var(--fs-sm)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
}

const iconBtn: React.CSSProperties = {
  padding: 8,
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--border)',
  background: 'none',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const pendingPill: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--surface-2)',
  color: 'var(--text-muted)',
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 'var(--fs-sm)',
}
